import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Account, Id } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { PlusIcon } from '@hero/web-icons';
import { InvoiceTableManager } from '../../Invoice/Table/InvoiceTableManager';
import { EligibleLegsDrawer } from '../../Invoice/EligibleLegs/EligibleLegsDrawer';
import { Button } from '../../../ui/Button/Button';

export interface AccountInvoicesTabProps {
  account: Account;
  /**
   * The viewer's role on the account, mapped onto the legacy
   * `'customer' | 'carrier'` axis used by `InvoiceTableManager`.
   * Until the Invoice surfaces flip to the bilateral `asRole`
   * prop, we keep the call site here.
   */
  displayForUser: 'customer' | 'carrier';
  /**
   * Optional counterparty CRM Client id (or linked-party id) — used
   * by the "Create invoice from delivered legs" picker to narrow
   * eligible legs to this client. When omitted, the picker is hidden
   * because the surface has no buyer-side to scope by.
   */
  counterpartyClientId?: Id;
  /**
   * Viewer org id — required to open the picker (which pins the
   * viewer as the seller server-side). When omitted, the picker is
   * hidden.
   */
  viewerOrgId?: Id;
}

/**
 * Invoices scoped to a single bilateral `Account`. Powered by
 * `InvoiceTableManager` with `scopeQuery={{ accountId }}` — the
 * shared manager owns search / status filtering, we just narrow
 * the data to this one pair.
 *
 * When both `counterpartyClientId` and `viewerOrgId` are provided,
 * a header row exposes a "Create invoice" button that opens the
 * {@link EligibleLegsDrawer} scoped to this Account's counterparty.
 * The backend enforces seller-only at the API layer, so the UI does
 * not gate on `displayForUser` here.
 */
export function AccountInvoicesTab({
  account,
  displayForUser,
  counterpartyClientId,
  viewerOrgId,
}: AccountInvoicesTabProps) {
  const { t } = useTranslation(['m-invoice', 'm-account']);
  const [open, setOpen] = useState(false);

  // Anyone in the workspace can offer the "Create invoice" picker.
  // The backend (`captureLegsForViewer`) enforces seller-side at the
  // API layer (it only emits eligible legs where the viewer is the
  // seller), so a frontend role gate would just hide the affordance
  // for legitimate sellers (e.g. carrier-dashboard users viewing
  // their own clients).
  const canCreate = !!counterpartyClientId && !!viewerOrgId;

  return (
    <div className="flex flex-col gap-3 px-4 py-3">
      {canCreate && (
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-medium text-gray-900">
            {t('m-account:tab.invoices_title', {
              defaultValue: 'Invoices',
            })}
          </h3>
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2">
            <PlusIcon className="size-4" />
            {t('m-invoice:create_invoice.button', {
              defaultValue: 'Create invoice',
            })}
          </Button>
        </div>
      )}

      <InvoiceTableManager
        displayForUser={displayForUser}
        scopeQuery={{ accountId: getObjectId(account._id) }}
      />

      {canCreate && (
        <EligibleLegsDrawer
          clientId={counterpartyClientId}
          viewerOrgId={viewerOrgId}
          open={open}
          onOpenChange={setOpen}
        />
      )}
    </div>
  );
}
